import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  FormErrorMessage,
  HStack,
  Input,
  VStack,
  Grid,
  Spinner,
  Text,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  IconButton,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/react";
import { Upload, X } from "lucide-react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
  "image/webp",
] as const;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_ATTACHMENT_FILES = 10;
const ACCEPT_ATTR = "image/jpeg,image/png,image/gif,image/webp,.jpeg,.jpg,.png,.gif,.webp";
const DOWNLOAD_REVOKE_DELAY_MS = 500;

/** If date is ISO string, parse and return yyyy-mm-dd in local time; otherwise return as-is. */
function normalizeApiDate(dateRaw: unknown): string {
  if (dateRaw == null) return "";
  if (typeof dateRaw !== "string") return "";
  const s = dateRaw.trim();
  if (!s) return "";
  if (s.includes("T")) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.substring(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return s.length >= 10 ? s.substring(0, 10) : s;
}

export interface DownloadInvoiceFormProps {
  /** Invoice ID (optional). When missing or invalid, form does not fetch, attach, or download. */
  invoiceId?: number;
  onSuccess?: () => void;
  onCancel: () => void;
}

export function DownloadInvoiceForm({
  invoiceId,
  onSuccess,
  onCancel,
}: DownloadInvoiceFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess } = useToastNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [displayInvoiceNumber, setDisplayInvoiceNumber] = useState("");
  const [displayRegisteredName, setDisplayRegisteredName] = useState("");
  const [displayDate, setDisplayDate] = useState("");
  const [displayVat, setDisplayVat] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);

  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const hasAttachments = attachmentFiles.length > 0;

  // Valid only if provided and is a finite number; no fetch/attach/download when invalid
  const safeInvoiceId =
    invoiceId != null && typeof invoiceId === "number" && Number.isFinite(invoiceId)
      ? invoiceId
      : null;

  function validateAttachmentFile(file: File): string | null {
    const type = file.type?.toLowerCase() ?? "";
    if (!ALLOWED_IMAGE_TYPES.includes(type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
      return `"${file.name}": must be JPEG, PNG, JPG, GIF, or WebP.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `"${file.name}": must be 5MB or less.`;
    }
    return null;
  }

  function handleAttachmentChange(e: React.ChangeEvent<HTMLInputElement>) {
    setAttachmentError(null);
    const input = e.target;
    const fileList = input.files;
    if (!fileList || fileList.length === 0) return;

    const next: File[] = [];
    const errors: string[] = [];
    for (let i = 0; i < fileList.length; i++) {
      if (next.length >= MAX_ATTACHMENT_FILES) {
        errors.push(`Maximum ${MAX_ATTACHMENT_FILES} files allowed.`);
        break;
      }
      const file = fileList[i];
      const err = validateAttachmentFile(file);
      if (err) errors.push(err);
      else next.push(file);
    }
    if (errors.length > 0) {
      setAttachmentError(errors.slice(0, 3).join(" "));
    }
    setAttachmentFiles((prev) => {
      const combined = [...prev, ...next].slice(0, MAX_ATTACHMENT_FILES);
      return combined;
    });
    input.value = "";
  }

  function removeAttachment(index: number) {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  }

  // Always fetch invoice by invoiceId and autofill read-only fields
  useEffect(() => {
    if (safeInvoiceId == null) return;
    isMountedRef.current = true;
    const fetchInvoice = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/invoices/${safeInvoiceId}`);
        if (!isMountedRef.current) return;
        const data = response.data?.data ?? response.data ?? {};

        const invoiceNumber =
          data.invoice_number ?? (data as Record<string, unknown>).invoice_number ?? "";
        const soa = data.statement_of_account ?? (data as Record<string, unknown>).statement_of_account;
        const shippingLine = data.shipping_line ?? soa?.shipping_line ?? (soa as Record<string, unknown>)?.shipping_line;
        const registeredName =
          shippingLine?.name ?? (shippingLine as Record<string, unknown>)?.name ?? "";

        const dateRaw = data.date ?? (data as Record<string, unknown>).date;
        const dateStr = normalizeApiDate(dateRaw);

        const discount = data.discount ?? (data as Record<string, unknown>).discount ?? 0;
        const vatStr =
          discount === 0 || discount === null || discount === undefined
            ? ""
            : String(discount);

        setDisplayInvoiceNumber(String(invoiceNumber));
        setDisplayRegisteredName(String(registeredName));
        setDisplayDate(dateStr);
        setDisplayVat(vatStr);
      } catch (error: unknown) {
        if (isMountedRef.current) showError(error);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    fetchInvoice();
    return () => {
      isMountedRef.current = false;
    };
  }, [safeInvoiceId, showError]);

  const handleDownload = async (format: "pdf" | "csv") => {
    if (safeInvoiceId == null) {
      showError(new Error("Invoice ID is required to download."));
      return;
    }

    const setIsDownloading =
      format === "pdf" ? setIsDownloadingPdf : setIsDownloadingCsv;
    const downloadUrl =
      format === "pdf"
        ? `/invoices/${safeInvoiceId}/download`
        : `/invoices/${safeInvoiceId}/download-csv`;
    const fileExtension = format === "pdf" ? "pdf" : "csv";
    const successMessage =
      format === "pdf"
        ? "Invoice PDF downloaded successfully."
        : "Invoice CSV downloaded successfully.";

    setIsDownloading(true);
    let objectUrl: string | null = null;
    const includeAttachments = hasAttachments;

    try {
      if (includeAttachments && attachmentFiles.length > 0) {
        setIsUploadingAttachments(true);
        try {
          const formData = new FormData();
          attachmentFiles.forEach((file) => {
            formData.append("attachments[]", file, file.name);
          });
          await apiClient.request({
            method: "POST",
            url: "/invoices/attachments",
            data: formData,
            timeout: 60000,
            headers: {
              "Content-Type": undefined,
            },
          });
        } catch (uploadError: unknown) {
          showError(uploadError as Error);
          return;
        } finally {
          setIsUploadingAttachments(false);
        }
      }

      const response = await apiClient.get(downloadUrl, {
        responseType: "blob",
        timeout: 60000,
        params: { include_attachments: includeAttachments },
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("Invalid response: expected a file.");
      }

      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.setAttribute(
        "download",
        `${displayInvoiceNumber || "invoice"}.${fileExtension}`,
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.setTimeout(() => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }, DOWNLOAD_REVOKE_DELAY_MS);

      if (isMountedRef.current) {
        showSuccess("Success", successMessage);
        onSuccess?.();
      }
    } catch (error: unknown) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      if (isMountedRef.current) showError(error as Error);
    } finally {
      if (isMountedRef.current) {
        setIsDownloading(false);
        setIsUploadingAttachments(false);
      }
    }
  };

  if (safeInvoiceId == null) {
    return (
      <Box p={4}>
        <Text color={colors.textSecondary}>
          {invoiceId == null
            ? "No invoice selected. Provide an invoice ID to download."
            : "Invalid invoice ID. Cannot fetch, attach, or download."}
        </Text>
        <Button mt={4} onClick={onCancel} variant="outline" size="sm">
          Cancel
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading invoice...
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Read-only invoice fields */}
      <Box>
        <VStack spacing={4} align="stretch">
          <Grid
            templateColumns={{ base: "1fr", md: "3fr 9fr" }}
            gap={4}
            alignItems="start"
          >
            <FormLabel pt={2} fontWeight="medium" color={colors.text}>
              Registered Name:
            </FormLabel>
            <FormControl>
              <Input
                value={displayRegisteredName}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>

          <Grid
            templateColumns={{ base: "1fr", md: "3fr 9fr" }}
            gap={4}
            alignItems="start"
          >
            <FormLabel pt={2} fontWeight="medium" color={colors.text}>
              Invoice Number:
            </FormLabel>
            <FormControl>
              <Input
                value={displayInvoiceNumber}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>

          <Grid
            templateColumns={{ base: "1fr", md: "3fr 9fr" }}
            gap={4}
            alignItems="start"
          >
            <FormLabel pt={2} fontWeight="medium" color={colors.text}>
              Date:
            </FormLabel>
            <FormControl>
              <Input
                value={displayDate}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>

          <Grid
            templateColumns={{ base: "1fr", md: "3fr 9fr" }}
            gap={4}
            alignItems="start"
          >
            <FormLabel pt={2} fontWeight="medium" color={colors.text}>
              Discount:
            </FormLabel>
            <FormControl>
              <Input
                value={displayVat}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>
        </VStack>
      </Box>

      {/* Attachments (optional) */}
      <FormControl isInvalid={!!attachmentError}>
        <VStack align="stretch" spacing={3}>
          <FormLabel fontWeight="medium" color={colors.text}>
            Attachments (optional)
          </FormLabel>
          <FormHelperText color={colors.textSecondary} mt={0}>
            JPEG, PNG, JPG, GIF, or WebP. Max 5MB each. Max {MAX_ATTACHMENT_FILES} files.
          </FormHelperText>
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              leftIcon={<Icon as={Upload} boxSize={4} />}
              onClick={() => fileInputRef.current?.click()}
              isDisabled={attachmentFiles.length >= MAX_ATTACHMENT_FILES}
              variant="outline"
              borderColor={colors.border}
              color={colors.text}
            >
              Choose images
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              multiple
              onChange={handleAttachmentChange}
              style={{ display: "none" }}
              aria-hidden
            />
            {attachmentFiles.length > 0 && (
              <Text fontSize="sm" color={colors.textSecondary}>
                {attachmentFiles.length} file(s) selected
              </Text>
            )}
          </HStack>
          <FormErrorMessage>{attachmentError ?? ""}</FormErrorMessage>
          {attachmentFiles.length > 0 && (
            <Wrap spacing={2}>
              {attachmentFiles.map((file, index) => (
                <WrapItem key={`${file.name}-${index}`}>
                  <Tag size="md" colorScheme="blue" borderRadius="full" gap={1}>
                    <TagLabel maxW="120px" noOfLines={1} title={file.name}>
                      {file.name}
                    </TagLabel>
                    <IconButton
                      aria-label={`Remove ${file.name}`}
                      size="xs"
                      variant="ghost"
                      icon={<Icon as={X} boxSize={3} />}
                      onClick={() => removeAttachment(index)}
                      minW={6}
                      minH={6}
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </VStack>
      </FormControl>

      {/* Action Buttons */}
      <HStack spacing={{ base: 2, md: 4 }} justify="flex-end" flexWrap="wrap" pt={2}>
        <Button
          variant="outline"
          onClick={onCancel}
          borderColor={colors.border}
          color={colors.text}
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleDownload("pdf")}
          colorScheme="blue"
          isLoading={isDownloadingPdf}
          loadingText={isUploadingAttachments ? "Uploading..." : "Downloading..."}
          isDisabled={
            safeInvoiceId == null || isDownloadingCsv || isUploadingAttachments
          }
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Download PDF
        </Button>
        <Button
          onClick={() => handleDownload("csv")}
          colorScheme="blue"
          isLoading={isDownloadingCsv}
          loadingText={isUploadingAttachments ? "Uploading..." : "Downloading..."}
          isDisabled={
            safeInvoiceId == null || isDownloadingPdf || isUploadingAttachments
          }
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Download CSV
        </Button>
      </HStack>
    </VStack>
  );
}
